import { AfterContentInit, ChangeDetectionStrategy, Component, Inject, NgZone, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { BaseComponent } from '@ridango/core';
import { LatLng, LocationEvent } from 'leaflet';
import { filter, take, takeUntil } from 'rxjs/operators';

import { ExtendedLocateOptions, MapApiService } from '../services';
import { MapCoordinates, OpenTripPlannerFacade } from '@ridango/components';
import Env from '@ridango/core/env';
import { Geolocation } from '@capacitor/geolocation';
import { Capacitor } from '@capacitor/core';
import { timer } from 'rxjs';

@Component({
    selector: 'rwidget-map-locator',
    template: ``,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MapLocatorComponent extends BaseComponent implements AfterContentInit, OnDestroy {
    private initLocationFound = false;
    private mapReady$ = this.mapApi.mapReady().pipe(filter((ready) => ready));
    private readonly initZoom = this.env.MAP?.INITIAL_ZOOM_MAP || 13;
    private readonly mapCenter: MapCoordinates = this.env.MAP.COORDINATES;
    private locatorStarted = false;
    private timeout: number;
    private permissionAsked = false;

    constructor(
        @Inject('ENV') public env: Env,
        private mapApi: MapApiService,
        private openTripPlannerFacade: OpenTripPlannerFacade,
        private translate: TranslateService,
        private zone: NgZone
    ) {
        super();
    }

    ngAfterContentInit(): void {
        this.mapReady$
            .pipe(
                takeUntil(this.componentDestroyed$),
                filter((v) => v),
                take(1)
            )
            .subscribe(() => this.init());
    }

    private async init() {
        await this.zone.runOutsideAngular(async () => {
            const options: ExtendedLocateOptions = {
                showPopup: false,
                clickBehavior: { inView: 'setView' },
                onLocationError: () => {
                    this.errorMapHandling();
                },
                position: this.env.MAP?.ZOOM_CONTROLS_HIDDEN ? 'topright' : 'topleft',
                ...(this.env.IS_NEW_AFC ? { setView: true, maxZoom: this.initZoom } : {})
            };

            if (!this.mapApi.isCenteringEnabled()) {
                options.setView = false;
            }

            const locator = this.mapApi.initLocate(options);

            this.mapApi.addToMap(locator);
            this.mapApi.addClass(locator.getContainer(), 'hidden');

            const button = locator?.getContainer()?.querySelector('.leaflet-bar-part-single') as HTMLElement;
            if (button) {
                button.setAttribute('aria-label', this.translate.instant('SHOW_ME_WHERE_I_AM'));
                button.removeAttribute('title');
            }

            this.openTripPlannerFacade.triggerLocationInit$.pipe(takeUntil(this.componentDestroyed$)).subscribe(() => {
                this.triggerLocation(locator);
            });

            this.openTripPlannerFacade.stopLocate$.pipe(takeUntil(this.componentDestroyed$)).subscribe(() => {
                this.onStopLocate(locator);
            });

            this.mapApi
                .addListenerSubscription('locationfound')
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe((e: LocationEvent) => this.handleLocationFound(e, locator));

            if (Capacitor.isNativePlatform()) {
                await this.triggerLocation(locator);
            } else {
                locator.start();
            }
        });
    }

    private onStopLocate(locator): void {
        this.locatorStarted && locator.stop();
        this.locatorStarted = false;
        this.initLocationFound = false;
        this.permissionAsked = false;
        this.mapApi.addClass(locator.getContainer(), 'hidden');
    }

    private handleLocationFound(e: LocationEvent | { latlng: LatLng }, locator) {
        this.locatorStarted = true;

        if (this.initLocationFound) return;

        const { latlng } = e;

        if (Capacitor.isNativePlatform() && this.env.MAP.MAX_BOUNDS && !this.mapApi.checkIsContainBound(latlng)) {
            this.mapApi.removeClass(locator.getContainer(), 'hidden');
            this.updateLocationAndReset(latlng);
            this.mapApi.centerMapToCoordinates(
                { lat: this.mapCenter.latitude, lng: this.mapCenter.longitude },
                this.initZoom
            );
            return;
        }

        this.mapApi.removeClass(locator.getContainer(), 'hidden');
        this.updateLocationAndReset(latlng);

        if (window.location.href.includes('/explore')) {
            const zoom = this.env.IS_NEW_AFC ? this.initZoom : undefined;
            this.mapApi.centerMapToCoordinates(e.latlng, zoom);
        }
    }

    private updateLocationAndReset(latlng: LatLng): void {
        this.initLocationFound = true;
        this.openTripPlannerFacade.setCurrentLocation(latlng);
    }

    async triggerLocation(locator) {
        if (this.initLocationFound) return;

        try {
            let permission = await Geolocation.checkPermissions();

            if (permission.location !== 'granted' && !this.permissionAsked) {
                permission = await Geolocation.requestPermissions();
                this.permissionAsked = true;
            }
            if (permission.location !== 'granted') {
                throw await Promise.reject();
            }

            if (Capacitor.isNativePlatform() && this.env.MAP.MAX_BOUNDS) {
                const isBoundLocation = await this.checkIsBound();
                if (isBoundLocation) {
                    this.mapApi.addClass(locator.getContainer(), 'hidden');
                    return;
                }
            }

            this.stopLocatorIfActive(locator);
            !this.locatorStarted && locator.start();
        } catch (e) {
            this.handleLocationError(locator);
        }
    }

    async checkIsBound(): Promise<boolean> {
        try {
            const { coords } = await Geolocation.getCurrentPosition();
            const latlng = new LatLng(coords.latitude, coords.longitude);
            const isWithinBound = this.mapApi.checkIsContainBound(latlng);
            if (isWithinBound) {
                return false;
            }
            // For init Map on EXPLORE page
            timer(300)
                .pipe(take(1))
                .subscribe(() => {
                    this.mapApi.centerMapToCoordinates(
                        { lat: this.mapCenter.latitude, lng: this.mapCenter.longitude },
                        this.initZoom
                    );
                });
            this.updateLocationAndReset(latlng);
            return true;
        } catch (e) {
            return false;
        }
    }

    private handleLocationError(locator) {
        this.locatorStarted && locator.stop();

        this.mapApi.addClass(locator.getContainer(), 'hidden');
        this.mapApi.centerMapToCoordinates(
            { lat: this.mapCenter.latitude, lng: this.mapCenter.longitude },
            this.initZoom
        );

        this.initLocationFound = true;
        this.openTripPlannerFacade.setCurrentLocation(null, true);
    }

    private stopLocatorIfActive(locator): void {
        if (locator.stopLocate && this.locatorStarted) {
            locator.stopLocate();
            this.locatorStarted = false;
        }
    }

    async errorMapHandling() {
        try {
            let permission = await Geolocation.checkPermissions();

            if (permission.location === 'prompt') {
                permission = await Geolocation.requestPermissions();
            }

            const isNotGranted = permission.location !== 'granted';
            this.openTripPlannerFacade.setCurrentLocation(null, isNotGranted);
        } catch (error) {
            this.initLocationFound = false;
            this.openTripPlannerFacade.setCurrentLocation(null, true);
        }
    }

    ngOnDestroy() {
        this.openTripPlannerFacade.triggerLocateStop();
        this.mapApi.setCenteringEnabled(true);
        clearTimeout(this.timeout);
        super.ngOnDestroy();
    }
}
